import { Delete as DeleteIcon, CloudUpload as UploadIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { collection, deleteDoc, doc, onSnapshot, orderBy, query } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/components/auth-guard';
import { Page } from '@/components/page';
import { useFirebase } from '@/firebase';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { useSubscription } from '@/hooks/useSubscription';

export const Route = createFileRoute('/import/')({
  component: ImportLandingPage,
});

function ImportLandingContent() {
  const { novel } = useNovelRepository();
  const { backend, firestore } = useFirebase();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const getSessionTypeLabel = (type: string): string => {
    switch (type) {
      case 'manuscript':
        return t('import.type_manuscript');
      case 'notes':
        return t('import.type_notes');
      case 'scene':
        return t('import.type_scene');
      case 'chapter':
        return t('import.type_chapter');
      case 'novel':
        return t('import.type_novel');
      case 'worldbuilding_notes':
        return t('import.type_worldbuilding');
      case 'character_notes':
        return t('import.type_characters');
      case 'mixed_content':
        return t('import.type_mixed');
      default:
        return type;
    }
  };

  const getSessionStatusLabel = (status: string): string => {
    switch (status) {
      case 'pending':
        return t('import.status_pending');
      case 'processing':
        return t('import.status_processing');
      case 'completed':
        return t('import.status_completed');
      case 'failed':
        return t('import.status_failed');
      case 'review':
        return t('import.status_review');
      case 'awaiting_review':
        return t('import.status_awaiting_review');
      default:
        return status;
    }
  };

  const [text, setText] = useState('');
  const [contentTypeHint, setContentTypeHint] = useState('mixed_content');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [pendingSessions, setPendingSessions] = useState<any[]>([]);
  const [deleteSessionId, setDeleteSessionId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (sessionId: string) => {
    setDeleteSessionId(sessionId);
  };

  const handleConfirmDelete = async () => {
    if (!novel.id || !firestore || !deleteSessionId) return;
    setIsDeleting(true);
    try {
      const docRef = doc(firestore, 'users', novel.ownerId, 'novels', novel.id, 'import_sessions', deleteSessionId);
      await deleteDoc(docRef);
      setDeleteSessionId(null);
    } catch (err: any) {
      console.error('Failed to delete import session:', err);
      setErrorMsg(err.message || t('import.error_failed'));
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    if (!novel.id || !firestore) {
      setPendingSessions([]);
      return;
    }

    const sessionsRef = collection(firestore, 'users', novel.ownerId, 'novels', novel.id, 'import_sessions');
    const q = query(sessionsRef, orderBy('updatedAt', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((s: any) => s.status !== 'completed' && s.status !== 'failed');
        setPendingSessions(list);
      },
      (err) => {
        console.error('Error loading pending sessions:', err);
      }
    );

    return () => unsubscribe();
  }, [novel, firestore]);

  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
  const charCount = text.length;

  const handleSubmit = async () => {
    if (!novel.id) return;
    if (charCount < 20) {
      setErrorMsg(t('import.error_too_short'));
      return;
    }

    setIsSubmitting(true);
    setErrorMsg('');

    try {
      const { sessionId } = await backend.ai.startNovelKnowledgeBuild({
        novelId: novel.id,
        text,
      });
      navigate({ to: '/import/processing/$sessionId', params: { sessionId } });
    } catch (err: any) {
      console.error('Failed to initiate import:', err);
      setErrorMsg(err.message || t('import.error_failed'));
      setIsSubmitting(false);
    }
  };

  if (!novel.id) {
    return (
      <Card sx={{ maxWidth: 500, mx: 'auto', mt: 4, textAlign: 'center', p: 4, borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h2" gutterBottom>
            {t('entity_list.no_novel_title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('entity_list.no_novel_desc', { entity: t('sidebar.import_material').toLowerCase() })}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Page
      title={t('sidebar.import_material')}
      description={t('import.landing_desc')}
      layoutMode="scrolling"
    >

      {pendingSessions.length > 0 && (
        <Card sx={{ mt: 3, borderRadius: 4, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h3" sx={{ mb: 3, fontWeight: 600 }}>
              {t('import.pending_sessions_title')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {pendingSessions.map((sessionItem) => {
                const isAwaiting = sessionItem.status === 'awaiting_review';
                let timeStr = '';
                if (sessionItem.updatedAt) {
                  if (typeof sessionItem.updatedAt.toDate === 'function') {
                    timeStr = sessionItem.updatedAt.toDate().toLocaleString();
                  } else if (sessionItem.updatedAt.seconds) {
                    timeStr = new Date(sessionItem.updatedAt.seconds * 1000).toLocaleString();
                  } else {
                    timeStr = new Date(sessionItem.updatedAt).toLocaleString();
                  }
                }
                return (
                  <Box
                    key={sessionItem.id}
                    sx={{
                      p: 2,
                      bgcolor: 'background.paper',
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {sessionItem.contentType
                            ? getSessionTypeLabel(sessionItem.contentType)
                            : t('import.unknown_type')}
                        </Typography>
                        <Chip
                          label={getSessionStatusLabel(sessionItem.status)}
                          color={isAwaiting ? 'warning' : 'primary'}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {timeStr}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('import.progress')}: {sessionItem.progress || 0}%
                      </Typography>
                      <Button
                        variant={isAwaiting ? 'contained' : 'outlined'}
                        color="primary"
                        size="small"
                        onClick={() => {
                          if (isAwaiting) {
                            navigate({ to: '/import/review/$sessionId', params: { sessionId: sessionItem.id } });
                          } else {
                            navigate({ to: '/import/processing/$sessionId', params: { sessionId: sessionItem.id } });
                          }
                        }}
                        sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
                      >
                        {isAwaiting ? t('import.resume_review') : t('import.view_progress')}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteClick(sessionItem.id)}
                        sx={{ borderRadius: 2, textTransform: 'none', px: 2 }}
                      >
                        {t('import.delete_session')}
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      )}

      <Card sx={{ mt: 3, borderRadius: 4, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <CardContent sx={{ p: 4 }}>
          {errorMsg && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 2 }}>
              <Typography variant="body2">{errorMsg}</Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="content-type-hint-label">
                {t('import.content_type_label')}
              </InputLabel>
              <Select
                labelId="content-type-hint-label"
                value={contentTypeHint}
                label={t('import.content_type_label')}
                onChange={(e) => setContentTypeHint(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="mixed_content">{t('import.type_mixed')}</MenuItem>
                <MenuItem value="novel">{t('import.type_novel')}</MenuItem>
                <MenuItem value="single_chapter">{t('import.type_chapter')}</MenuItem>
                <MenuItem value="scene">{t('import.type_scene')}</MenuItem>
                <MenuItem value="worldbuilding_notes">{t('import.type_worldbuilding')}</MenuItem>
                <MenuItem value="character_notes">{t('import.type_characters')}</MenuItem>
              </Select>
            </FormControl>

            <TextField
              id="import-text-input"
              label={t('import.paste_label')}
              multiline
              rows={15}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                if (errorMsg) setErrorMsg('');
              }}
              variant="outlined"
              fullWidth
              disabled={isSubmitting}
              placeholder={t('import.paste_placeholder')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  fontFamily: 'Georgia, serif',
                  fontSize: '1.05rem',
                  lineHeight: 1.6,
                },
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {wordCount.toLocaleString()} {t('import.words')} | {charCount.toLocaleString()}{' '}
                {t('import.characters')}
              </Typography>

              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleSubmit}
                disabled={isSubmitting || charCount < 20}
                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
                sx={{
                  borderRadius: 2.5,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                }}
              >
                {isSubmitting
                  ? t('import.btn_submitting')
                  : t('import.btn_submit')}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={Boolean(deleteSessionId)}
        onClose={() => !isDeleting && setDeleteSessionId(null)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1.5,
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {t('import.delete_confirm_title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('import.delete_confirm_desc')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ gap: 1, px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteSessionId(null)}
            disabled={isDeleting}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            {t('import.delete_cancel_btn')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            {t('import.delete_confirm_btn')}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}

export default function ImportLandingPage() {
  const { hasFeature, loading } = useSubscription();
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasFeature('ai_assistance_allowed')) {
    return (
      <Card sx={{ maxWidth: 500, mx: 'auto', mt: 8, textAlign: 'center', p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Typography variant="h2" gutterBottom color="error.main" sx={{ fontWeight: 600 }}>
            {t('import.access_denied_title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, mt: 1 }}>
            {t('import.access_denied_desc')}
          </Typography>
          <Button variant="contained" color="primary" onClick={() => navigate({ to: '/' })} sx={{ borderRadius: 2 }}>
            {t('import.return_to_dashboard')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <AuthGuard>
      <ImportLandingContent />
    </AuthGuard>
  );
}
